// Salesforce Connection Component
// OAuth connection, sync settings, and status display

import React, { useState, useEffect } from 'react';
import { Cloud, CheckCircle, XCircle, RefreshCw, Settings, Zap, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase.js';
import styles from './SalesforceConnection.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export default function SalesforceConnection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState(null);

  const [syncContacts, setSyncContacts] = useState(true);
  const [syncEvents, setSyncEvents] = useState(true);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);

  useEffect(() => {
    loadStatus();

    const params = new URLSearchParams(window.location.search);
    if (params.get('salesforce_connected') === 'true') {
      setError(null);
      loadStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('salesforce_error')) {
      setError(params.get('salesforce_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function loadStatus() {
    try {
      setLoading(true);
      const session = await supabase.auth.getSession();

      const response = await fetch(`${API_URL}/v1/salesforce/status`, {
        headers: { 'Authorization': `Bearer ${session.data.session.access_token}` }
      });

      const data = await response.json();
      setStatus(data);

      if (data.settings) {
        setSyncContacts(data.settings.syncContacts);
        setSyncEvents(data.settings.syncEvents);
        setAutoSyncEnabled(data.settings.autoSyncEnabled);
      }
    } catch (err) {
      console.error('Failed to load Salesforce status:', err);
    } finally {
      setLoading(false);
    }
  }

  async function connectSalesforce() {
    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.user?.id) { setError('Not authenticated'); return; }
      const userId = session.data.session.user.id;
      window.location.href = `${API_URL}/v1/salesforce/connect?user_id=${userId}&cb=${Date.now()}`;
    } catch (err) {
      setError('Failed to initiate Salesforce connection');
      console.error(err);
    }
  }

  async function disconnectSalesforce() {
    if (!confirm('Are you sure you want to disconnect Salesforce? This will stop automatic syncing.')) return;

    try {
      setLoading(true);
      const session = await supabase.auth.getSession();
      const response = await fetch(`${API_URL}/v1/salesforce/disconnect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.data.session.access_token}` }
      });

      if (response.ok) {
        setStatus({ connected: false });
        setError(null);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to disconnect');
      }
    } catch (err) {
      setError('Failed to disconnect Salesforce');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function manualSync() {
    try {
      setSyncing(true);
      setError(null);
      const session = await supabase.auth.getSession();
      const response = await fetch(`${API_URL}/v1/salesforce/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.data.session.access_token}` }
      });

      const data = await response.json();
      if (response.ok) {
        alert(`Sync complete!\n\nSynced: ${data.synced} bookings\nFailed: ${data.failed} bookings`);
        loadStatus();
      } else {
        setError(data.message || 'Sync failed');
      }
    } catch (err) {
      setError('Failed to sync with Salesforce');
      console.error(err);
    } finally {
      setSyncing(false);
    }
  }

  async function updateSettings() {
    try {
      setLoading(true);
      const session = await supabase.auth.getSession();
      const response = await fetch(`${API_URL}/v1/salesforce/settings`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.data.session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ syncContacts, syncEvents, autoSyncEnabled })
      });

      if (response.ok) {
        setShowSettings(false);
        loadStatus();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update settings');
      }
    } catch (err) {
      setError('Failed to update settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  if (loading && !status) {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Cloud className={styles.headerIcon} size={24} />
            <h3 className={styles.headerTitle}>Salesforce CRM</h3>
          </div>
        </div>
        <div className={styles.loadingBody}>
          <div className={styles.spinner} />
          <p className={styles.spinnerText}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Cloud className={styles.headerIcon} size={24} />
          <div>
            <h3 className={styles.headerTitle}>Salesforce CRM</h3>
            <p className={styles.headerDesc}>
              {status?.connected
                ? 'Automatically sync bookings to Salesforce'
                : 'Connect to sync bookings as Contacts and Events'}
            </p>
          </div>
        </div>
        {status?.connected && (
          <div className={styles.connectedBadge}>
            <CheckCircle size={16} />
            Connected
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className={styles.errorBox}>
          <AlertCircle className={styles.errorIcon} size={20} style={{ flexShrink: 0, marginTop: 2 }} />
          <div className={styles.errorBody}>
            <p className={styles.errorTitle}>Connection Error</p>
            <p className={styles.errorText}>{error}</p>
          </div>
          <button onClick={() => setError(null)} className={styles.errorClose}>×</button>
        </div>
      )}

      {/* Body */}
      <div className={styles.body}>
        {!status?.connected ? (
          /* Not connected */
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrap}>
              <Cloud className={styles.emptyIcon} size={32} />
            </div>
            <h4 className={styles.emptyTitle}>Connect Salesforce</h4>
            <p className={styles.emptyDesc}>
              Automatically create Contacts and Events in Salesforce when customers book meetings through Vennio.
            </p>

            <button onClick={connectSalesforce} className={styles.connectBtn}>
              <Cloud size={20} />
              Connect Salesforce Account
            </button>

            <div className={styles.benefitsGrid}>
              <div className={styles.benefitCard}>
                <Zap className={styles.benefitIcon} size={20} />
                <h5 className={styles.benefitTitle}>Automatic Sync</h5>
                <p className={styles.benefitDesc}>Bookings sync instantly to Salesforce</p>
              </div>
              <div className={styles.benefitCard}>
                <CheckCircle className={styles.benefitIcon} size={20} />
                <h5 className={styles.benefitTitle}>Contact Deduplication</h5>
                <p className={styles.benefitDesc}>Merges duplicate contacts by email</p>
              </div>
              <div className={styles.benefitCard}>
                <RefreshCw className={styles.benefitIcon} size={20} />
                <h5 className={styles.benefitTitle}>Error Recovery</h5>
                <p className={styles.benefitDesc}>Automatic retry with detailed logging</p>
              </div>
            </div>
          </div>
        ) : (
          /* Connected */
          <div className={styles.connectedState}>
            <div className={styles.infoGrid}>
              <div className={styles.infoCard}>
                <div className={styles.infoLabel}>Organization ID</div>
                <div className={styles.infoValueMono}>{status.organizationId || 'Unknown'}</div>
              </div>
              <div className={styles.infoCard}>
                <div className={styles.infoLabel}>Last Sync</div>
                <div className={styles.infoValue}>
                  {status.lastSync ? new Date(status.lastSync).toLocaleString() : 'Never'}
                </div>
              </div>
            </div>

            {status.lastError && (
              <div className={styles.warningBox}>
                <AlertCircle className={styles.warningIcon} size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <p className={styles.warningTitle}>Last Sync Error</p>
                  <p className={styles.warningText}>{status.lastError}</p>
                </div>
              </div>
            )}

            {showSettings ? (
              <div className={styles.settingsPanel}>
                <h4 className={styles.settingsTitle}>Sync Settings</h4>
                <div className={styles.settingsList}>
                  {[
                    { label: 'Auto-sync Enabled', hint: 'Automatically sync new bookings', value: autoSyncEnabled, setter: setAutoSyncEnabled },
                    { label: 'Sync Contacts', hint: 'Create/update Contact records', value: syncContacts, setter: setSyncContacts },
                    { label: 'Sync Events', hint: 'Create Event records linked to Contacts', value: syncEvents, setter: setSyncEvents },
                  ].map(({ label, hint, value, setter }) => (
                    <label key={label} className={styles.settingLabel}>
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setter(e.target.checked)}
                        className={styles.settingCheckbox}
                      />
                      <div>
                        <div className={styles.settingLabelTitle}>{label}</div>
                        <div className={styles.settingLabelHint}>{hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className={styles.settingsBtns}>
                  <button onClick={updateSettings} disabled={loading} className={styles.settingsSaveBtn}>
                    {loading ? 'Saving...' : 'Save Settings'}
                  </button>
                  <button onClick={() => setShowSettings(false)} className={styles.settingsCancelBtn}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.settingsSummary}>
                <div className={styles.settingSummaryItems}>
                  {[
                    { label: 'Auto-sync', on: status.settings?.autoSyncEnabled },
                    { label: 'Contacts',  on: status.settings?.syncContacts },
                    { label: 'Events',    on: status.settings?.syncEvents },
                  ].map(({ label, on }) => (
                    <div key={label} className={styles.settingSummaryItem}>
                      {on
                        ? <CheckCircle size={16} className={styles.summaryIconOn} />
                        : <XCircle size={16} className={styles.summaryIconOff} />}
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setShowSettings(true)} className={styles.configureBtn}>
                  <Settings size={16} />
                  Configure
                </button>
              </div>
            )}

            {status.recentSyncs && status.recentSyncs.length > 0 && (
              <div className={styles.recentSyncs}>
                <h4 className={styles.recentSyncsTitle}>Recent Sync Activity</h4>
                <div className={styles.syncList}>
                  {status.recentSyncs.slice(0, 5).map((sync) => (
                    <div key={sync.id} className={styles.syncItem}>
                      <div className={styles.syncItemLeft}>
                        {sync.status === 'success'
                          ? <CheckCircle size={16} className={styles.syncIconSuccess} />
                          : <XCircle size={16} className={styles.syncIconError} />}
                        <span className={styles.syncType}>{sync.sync_type}</span>
                        {sync.error_message && (
                          <span className={styles.syncError}>{sync.error_message}</span>
                        )}
                      </div>
                      <span className={styles.syncTime}>{new Date(sync.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.actions}>
              <button onClick={manualSync} disabled={syncing} className={styles.accentBtn}>
                <RefreshCw size={16} className={syncing ? styles.spinIcon : undefined} />
                {syncing ? 'Syncing...' : 'Manual Sync'}
              </button>
              <button onClick={disconnectSalesforce} className={styles.secondaryBtn}>
                Disconnect
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
